import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Header, Footer, CategoryIcon, MobileBottomNav } from '../components/Layout';
import { betsApi, walletApi, rpsApi } from '../services/api';
import { useWebSocket } from '../context/WebSocketContext';
import OnlinePlayersPanel from '../components/OnlinePlayers';
import VerifyEmailBanner from '../components/VerifyEmailBanner';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Plus, Wallet, Trophy, TrendingUp, Clock, Users, 
  ArrowRight, Sparkles, Target
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const { addListener } = useWebSocket() || {};
  const [wallet, setWallet] = useState(null);
  const [myBets, setMyBets] = useState({ created: [], matched: [] });
  const [trendingBets, setTrendingBets] = useState([]);
  const [rpsLeaderboard, setRpsLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashData = async () => {
    try {
      const [walletRes, myBetsRes, trendingRes, leaderboardRes] = await Promise.all([
        walletApi.getBalance(),
        betsApi.getMyBets(),
        betsApi.getTrending(5),
        rpsApi.getLeaderboard(5)
      ]);
      
      setWallet(walletRes.data);
      setMyBets(myBetsRes.data);
      setTrendingBets(trendingRes.data);
      setRpsLeaderboard(leaderboardRes.data);
    } catch (err) {
      if (loading) toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashData();
  }, []);

  // Real-time updates via WebSocket
  useEffect(() => {
    if (!addListener) return;
    return addListener((event) => {
      if (['bet_matched', 'your_bet_matched', 'your_bet_approved', 'bet_approved', 'rps_completed'].includes(event.type)) {
        fetchDashData();
      }
    });
  }, [addListener]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A]">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const activeBets = [...myBets.created, ...myBets.matched].filter(
    b => b.status === 'active' || b.status === 'open'
  );
  const totalWins = user?.wins || 0;
  const totalLosses = user?.losses || 0;

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex flex-col pb-20 md:pb-0">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <VerifyEmailBanner />
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-black uppercase text-white" style={{ fontFamily: 'Outfit' }} data-testid="dashboard-welcome">
            Welcome back, <span className="text-white">{user?.full_name?.split(' ')[0]}</span>!
          </h1>
          <p className="text-gray-400 mt-1">Ready to back your opinions?</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
          <div className="stat-card" data-testid="wallet-real-balance">
            <div className="stat-icon">
              <Wallet className="h-5 w-5 text-[#1A1A1A]" />
            </div>
            <div className="stat-value">${wallet?.real_balance?.toFixed(2) || '0.00'}</div>
            <div className="text-sm text-gray-400 uppercase font-semibold">Wallet Balance</div>
          </div>

          <div className="stat-card" data-testid="wallet-qbean-balance">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #28A745 0%, #1E7E34 100%)' }}>
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div className="stat-value" style={{ color: '#28A745' }}>{wallet?.qbean_balance?.toFixed(0) || '0'} QBC</div>
            <div className="text-sm text-gray-400 uppercase font-semibold">Qbean Tokens</div>
          </div>

          <div className="stat-card" data-testid="escrow-balance">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #B8960C 100%)' }}>
              <Clock className="h-5 w-5 text-[#1A1A1A]" />
            </div>
            <div className="stat-value" style={{ color: '#D4AF37' }}>{wallet?.escrow_locked?.toFixed(0) || '0'} QBC</div>
            <div className="text-sm text-gray-400 uppercase font-semibold">In Escrow</div>
          </div>

          <div className="stat-card" data-testid="active-bets-count">
            <div className="stat-icon">
              <Target className="h-5 w-5 text-[#1A1A1A]" />
            </div>
            <div className="stat-value">{activeBets.length}</div>
            <div className="text-sm text-gray-400 uppercase font-semibold">Active Bets</div>
          </div>

          <div className="stat-card" data-testid="win-rate">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #28A745 0%, #1E7E34 100%)' }}>
              <Trophy className="h-5 w-5 text-white" />
            </div>
            <div className="stat-value" style={{ color: '#28A745' }}>
              {totalWins + totalLosses > 0 
                ? Math.round((totalWins / (totalWins + totalLosses)) * 100)
                : 0}%
            </div>
            <div className="text-sm text-gray-400 uppercase font-semibold">Win Rate</div>
            <div className="text-xs text-gray-500">{totalWins}W / {totalLosses}L</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Link to="/create-bet">
            <div className="card-dark card-dark-interactive flex items-center justify-between bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 border-[#D4AF37]" data-testid="quick-create-bet">
              <div>
                <h3 className="font-bold text-lg uppercase text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>Create a Bet</h3>
                <p className="text-gray-400 text-sm">Bet on anything. Set your own odds.</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center">
                <Plus className="h-6 w-6 text-[#1A1A1A]" />
              </div>
            </div>
          </Link>
          
          <Link to="/rps">
            <div className="card-dark card-dark-interactive flex items-center justify-between bg-gradient-to-r from-[#28A745]/20 to-[#28A745]/5 border-[#28A745]" data-testid="quick-rps">
              <div>
                <h3 className="font-bold text-lg uppercase text-[#28A745]" style={{ fontFamily: 'Outfit' }}>Play RPS Live</h3>
                <p className="text-gray-400 text-sm">Rock Paper Scissors for real money</p>
              </div>
              <span className="text-3xl">✊✌️🖐️</span>
            </div>
          </Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* My Active Bets */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold uppercase text-white" style={{ fontFamily: 'Outfit' }}>My Active Bets</h2>
              <Link to="/browse" className="text-[#D4AF37] text-sm font-semibold flex items-center gap-1 hover:underline">
                View All <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            
            {activeBets.length === 0 ? (
              <div className="card-dark text-center py-12">
                <Target className="h-12 w-12 mx-auto mb-4 text-gray-500" />
                <p className="text-gray-400 mb-4">No active bets yet</p>
                <Link to="/browse">
                  <Button className="btn-primary">Browse Bets</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {activeBets.slice(0, 5).map((bet) => (
                  <Link key={bet.id} to={`/bet/${bet.id}`}>
                    <div className="card-dark card-dark-interactive" data-testid={`bet-card-${bet.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#2D2D2D] rounded-lg flex items-center justify-center border border-[#D4AF37]/30">
                            <CategoryIcon category={bet.category} className="h-5 w-5 text-[#D4AF37]" />
                          </div>
                          <div>
                            <span className="badge-gold text-xs">{bet.category}</span>
                            <h3 className="font-bold text-white mt-1">{bet.title}</h3>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-[#D4AF37]">
                            {bet.stake_type === 'real' ? '$' : ''}{bet.stake_amount}{bet.stake_type === 'qbean' ? ' QBC' : ''}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                            <Clock className="h-3 w-3" />
                            {new Date(bet.deadline).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-4 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                          bet.status === 'active' ? 'bg-[#28A745] text-white' : 
                          bet.status === 'open' ? 'bg-[#D4AF37] text-[#1A1A1A]' : 
                          'bg-gray-600 text-white'
                        }`}>
                          {bet.status}
                        </span>
                        <span className="text-gray-400">
                          <span className="text-gray-400 text-xs">{bet.category}</span>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Trending Bets */}
            <div>
              <h2 className="text-xl font-bold uppercase mb-4 text-white" style={{ fontFamily: 'Outfit' }}>
                <TrendingUp className="h-5 w-5 inline mr-2 text-[#D4AF37]" />
                Trending
              </h2>
              <div className="space-y-3">
                {trendingBets.slice(0, 4).map((bet) => (
                  <Link key={bet.id} to={`/bet/${bet.id}`}>
                    <div className="card-dark card-dark-interactive p-4" data-testid={`trending-bet-${bet.id}`}>
                      <span className="badge-gold text-xs">{bet.category}</span>
                      <h4 className="font-semibold text-sm text-white line-clamp-2 mt-2">{bet.title}</h4>
                      <div className="flex justify-between mt-2 text-xs text-gray-400">
                        <span className="text-[#D4AF37] font-bold">${bet.stake_amount}</span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {bet.matched ? 'Matched' : 'Open'}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <Link to="/browse" className="block mt-4">
                <Button className="btn-secondary w-full text-sm">View All Bets</Button>
              </Link>
            </div>

            {/* RPS Leaderboard */}
            <div>
              <h2 className="text-xl font-bold uppercase mb-4 text-white" style={{ fontFamily: 'Outfit' }}>
                <Trophy className="h-5 w-5 inline mr-2 text-[#28A745]" />
                RPS Champions
              </h2>
              <div className="card-dark">
                {rpsLeaderboard.length === 0 ? (
                  <p className="text-center text-gray-400 py-4">No champions yet</p>
                ) : (
                  <div className="space-y-3">
                    {rpsLeaderboard.map((player, idx) => (
                      <div key={player.id} className="flex items-center justify-between" data-testid={`leaderboard-${idx}`}>
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-[#D4AF37] text-[#1A1A1A]' :
                            idx === 1 ? 'bg-gray-400 text-gray-800' :
                            idx === 2 ? 'bg-amber-700 text-white' :
                            'bg-[#2D2D2D] text-gray-400'
                          }`}>
                            {idx + 1}
                          </span>
                          <span className="font-semibold text-white">{player.full_name}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-[#28A745]">{player.wins}W</div>
                          <div className="text-xs text-gray-500">
                            {Math.round(player.win_rate * 100)}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Link to="/rps" className="block mt-4">
                <Button className="btn-green w-full text-sm">Play Now</Button>
              </Link>
            </div>

            {/* Online Players */}
            <OnlinePlayersPanel />
          </div>
        </div>
      </main>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default Dashboard;
